// A window whose open state is in the URL (`?edit=…`, `?addto=…`): opening it is a history PUSH, so the browser's Back closes it;
// closing it from inside (X, Esc, Cancel, after a save) goes back when it was opened in this session, and otherwise (linked to
// directly) replaces the entry without it.
// A view switch inside the window (another entity, a filter, a sort) is a history step too (`step`): Back undoes it. The
// history entry's state counts the entries the window pushed (`<key>Opened`: 1 when opened, +1 per step), so closing goes back
// past all of them to where the window was opened from.
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from './liveparams.ts';
import { modalSteps } from './viewstate.ts';

export function useModalSteps(mark: string) {
  const [, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? {}) as Record<string, unknown>;
  const n = modalSteps(state[mark]);
  /** Opens: a push whose entry says it was opened here. */
  const open = useCallback((fn: (p: URLSearchParams) => URLSearchParams) => {
    setParams(fn, { state: { ...state, [mark]: 1 } });
  }, [setParams, state, mark]);
  /** A view switch inside: a push counted with the window (linked to directly, it can only replace). */
  const step = useCallback((fn: (p: URLSearchParams) => URLSearchParams) => {
    if (n > 0) setParams(fn, { state: { ...state, [mark]: n + 1 } });
    else setParams(fn, { replace: true, state });
  }, [setParams, state, mark, n]);
  /** Closes: back past every entry the window pushed, else (linked to directly) the entry without it. */
  const close = useCallback((fn: (p: URLSearchParams) => URLSearchParams) => {
    if (n > 0) { void navigate(-n); return; }
    const { [mark]: _gone, ...rest } = state;
    setParams(fn, { replace: true, state: rest });
  }, [n, navigate, setParams, mark, state]);
  return { opened: n > 0, open, step, close };
}

export function useUrlModal(key: string, read: (p: URLSearchParams) => string | undefined, write: (p: URLSearchParams, v: string | undefined) => URLSearchParams) {
  const [params] = useSearchParams();
  const m = useModalSteps(`${key}Opened`);
  const open = useCallback((value: string) => m.open((p) => write(p, value)), [m, write]);
  const close = useCallback(() => m.close((p) => write(p, undefined)), [m, write]);
  return { value: read(params), open, close, opened: m.opened, step: m.step };
}
